/**
 * /de/comparisons/confluence — Draftlize vs Confluence (German).
 * Targets confluence alternativen (90) / alternative zu confluence (70).
 * Links to /de/entscheidungsprotokoll.
 */
import { MarketingShell } from '../MarketingShell';

export function ConfluenceDe() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/comparisons/confluence"
      hreflangLocales={['de']}
      documentTitle="Confluence-Alternative für Produktteams: Draftlize · Draftlize"
      documentDescription="Confluence ist ein Wiki; Produktarbeit braucht verknüpfte, aktuelle Entscheidungen. Warum Produktteams von Confluence zu Draftlize wechseln — der Vergleich."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Vergleich · vs Confluence</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Die Confluence-Alternative,<br />
            <em>die für Produktarbeit gebaut ist.</em>
          </h1>
          <p className="lede">
            Confluence ist ein solides Unternehmens-Wiki: Seiten, Spaces, Rechte, Atlassian-Integration. Aber Produktarbeit braucht mehr als Seiten – sie braucht <em>Entscheidungen</em>, die verknüpft und aktuell bleiben. Draftlize ist ein Substrat, in dem jede Entscheidung eine adressierbare Karte ist, die „stale“ wird, wenn sich etwas ändert, und die dein KI-Agent lesen kann. Kurz: Confluence speichert, Draftlize hält aktuell.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Kurzfassung</span>
            <h2 className="serif">Direkt nebeneinander, <em>7 Dimensionen.</em></h2>
          </div>
          <p>Wiki-Dokumentation gegen lebendiges Entscheidungs-Substrat – zwei verschiedene Jobs.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Confluence</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Form</td><td className="ours yes">Typisierter Karten-Graph</td><td className="partial">Wiki-Seiten in Spaces</td></tr>
            <tr><td>Entscheidung als adressierbare Einheit</td><td className="ours yes">nativ</td><td className="no">Seite als Fließtext</td></tr>
            <tr><td>Stale-Propagation bei Änderung</td><td className="ours yes">nativ</td><td className="no">—</td></tr>
            <tr><td>KI liest/schreibt Entscheidungen</td><td className="ours yes">nativ via MCP</td><td className="no">—</td></tr>
            <tr><td>Unternehmens-Wiki · Spaces · Rechte</td><td className="no">Solo (v1)</td><td className="yes">exzellent</td></tr>
            <tr><td>Atlassian-Ökosystem (Jira etc.)</td><td className="no">—</td><td className="yes">tief integriert</td></tr>
            <tr><td>Lange Doku, Runbooks, Onboarding</td><td className="no">—</td><td className="yes">stark</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Wo <em>Confluence gewinnt.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Als Unternehmens-Wiki</h3>
            <p>Für Runbooks, Onboarding-Doku und langlebige Team-Seiten mit feinen Rechten ist Confluence stark. Draftlize will kein Wiki sein.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Im Atlassian-Stack</h3>
            <p>Wenn dein Team in Jira lebt, ist die Confluence-Integration ein echter Vorteil. Draftlize spielt bewusst eine andere Rolle.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Große Teams mit Governance</h3>
            <p>Berechtigungen, Freigaben, Versionsstände über viele Nutzer – dafür ist Confluence gebaut. Draftlize ist Solo-first.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Wo <em>Draftlize gewinnt.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Entscheidungen statt driftender Seiten</h3>
            <p>Eine Confluence-Seite lügt sechs Wochen später still weiter. In Draftlize ist jede Entscheidung eine Karte, die „stale“ wird, sobald eine spätere sie kippt.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Abhängigkeiten, die sich melden</h3>
            <p>Ändert sich eine Entscheidung, flaggen die abhängigen Karten und Specs automatisch. Kein stilles Auseinanderdriften von Doku und Realität.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">KI-Agenten lesen die Entscheidungen</h3>
            <p>Confluence-Seiten sind für Menschen. Draftlize-Karten liest Claude Code oder Cursor via MCP. Weiter zum <a href="/de/entscheidungsprotokoll" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Entscheidungsprotokoll</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Confluence ist die Antwort auf „Wo dokumentiert das Team“. Draftlize ist die Antwort auf „Wo bleiben die Produktentscheidungen aktuell“.
        </blockquote>
        <cite>Brauchst du nur ein Wiki, reicht Confluence.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Gratis mit <em>$5 starten.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Behalte Confluence fürs Wiki. Nimm Draftlize für die Produktentscheidungen darunter – als Karten, die aktuell bleiben. $5 gratis, zahle nur, was du nutzt.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
